import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { MessageCircle, ArrowRight, ArrowLeft, CheckCircle, Shield, Swords, Ghost, HeartHandshake } from "lucide-react";
import ExerciseIntro from "../components/ExerciseIntro";
import { getExerciseInfo } from "../lib/exerciseData";
import { API } from "../App";
import { useI18n } from "../lib/i18n";
import ShareExerciseDialog from "../components/ShareExerciseDialog";
import { useSharePrompt } from "../hooks/useSharePrompt";

const AssertivenessPage = ({ user, logout }) => {
  const { t } = useI18n();
  const { shareData, promptShare, closeSharePrompt } = useSharePrompt();
  const EXERCISE_INFO = getExerciseInfo(t);
  const [step, setStep] = useState(0);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    situation: "",
    emotions: "",
    current_style: "",
    describe: "",
    express: "",
    specify: "",
    consequences: "",
    reformulated_response: "",
    reflection: "",
  });

  const COMMUNICATION_STYLES = [
    {
      id: "passive",
      name: t("assertiveness.passive"),
      description: t("assertiveness.passive.description"),
      icon: Shield,
      color: "bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800",
      examples: t("assertiveness.passive.examples").split(","),
      consequence: t("assertiveness.passive.consequence"),
    },
    {
      id: "aggressive",
      name: t("assertiveness.aggressive"),
      description: t("assertiveness.aggressive.description"),
      icon: Swords,
      color: "bg-red-100 text-red-700 border-red-300 dark:bg-red-950 dark:text-red-300 dark:border-red-800",
      examples: t("assertiveness.aggressive.examples").split(","),
      consequence: t("assertiveness.aggressive.consequence"),
    },
    {
      id: "passive-aggressive",
      name: t("assertiveness.passiveAggressive"),
      description: t("assertiveness.passiveAggressive.description"),
      icon: Ghost,
      color: "bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-950 dark:text-purple-300 dark:border-purple-800",
      examples: t("assertiveness.passiveAggressive.examples").split(","),
      consequence: t("assertiveness.passiveAggressive.consequence"),
    },
    {
      id: "assertive",
      name: t("assertiveness.assertive"),
      description: t("assertiveness.assertive.description"),
      icon: HeartHandshake,
      color: "bg-green-100 text-green-700 border-green-300 dark:bg-green-950 dark:text-green-300 dark:border-green-800",
      examples: t("assertiveness.assertive.examples").split(","),
      consequence: t("assertiveness.assertive.consequence"),
    },
  ];

  const DESC_STEPS = [
    { key: "describe", label: t("assertiveness.describe"), detail: t("assertiveness.describeDetail"), placeholder: t("assertiveness.descPlaceholderD") },
    { key: "express", label: t("assertiveness.express"), detail: t("assertiveness.expressDetail"), placeholder: t("assertiveness.descPlaceholderE") },
    { key: "specify", label: t("assertiveness.specify"), detail: t("assertiveness.specifyDetail"), placeholder: t("assertiveness.descPlaceholderS") },
    { key: "consequences", label: t("assertiveness.consequences"), detail: t("assertiveness.consequencesDetail"), placeholder: t("assertiveness.descPlaceholderC") },
  ];

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      const res = await axios.get(`${API}/assertiveness`, { withCredentials: true });
      setEntries(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/assertiveness`, formData, { withCredentials: true });
      toast.success(t("assertiveness.saved"));
      if (user?.role === "patient") promptShare(res.data, "assertiveness");
      setFormData({ situation: "", emotions: "", current_style: "", describe: "", express: "", specify: "", consequences: "", reformulated_response: "", reflection: "" });
      setStep(0);
      fetchEntries();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setSubmitting(false); }
  };

  const STEPS_CONFIG = [
    {
      title: t("assertiveness.steps.situation"),
      content: (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{t("assertiveness.situationLabel")}</Label>
            <Textarea placeholder={t("assertiveness.situationPlaceholder")} value={formData.situation} onChange={(e) => setFormData({ ...formData, situation: e.target.value })} rows={3} data-testid="situation-input" />
          </div>
          <div className="space-y-2">
            <Label>{t("assertiveness.emotionsLabel")}</Label>
            <Input placeholder={t("assertiveness.emotionsPlaceholder")} value={formData.emotions} onChange={(e) => setFormData({ ...formData, emotions: e.target.value })} data-testid="emotions-input" />
          </div>
        </div>
      ),
    },
    {
      title: t("assertiveness.steps.style"),
      content: (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{t("assertiveness.styleQuestion")}</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {COMMUNICATION_STYLES.map((style) => (
              <button
                key={style.id}
                onClick={() => setFormData({ ...formData, current_style: style.id })}
                className={`p-4 rounded-lg border text-left transition-all ${formData.current_style === style.id ? style.color : "bg-background border-border hover:bg-muted"}`}
                data-testid={`style-${style.id}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <style.icon className="w-5 h-5" />
                  <span className="font-medium">{style.name}</span>
                </div>
                <p className="text-xs mb-2">{style.description}</p>
                <p className="text-xs font-medium">{t("assertiveness.examplesLabel")}</p>
                <ul className="text-xs list-disc pl-4">
                  {style.examples.map((ex, i) => <li key={i}>{ex}</li>)}
                </ul>
              </button>
            ))}
          </div>
        </div>
      ),
    },
    {
      title: t("assertiveness.steps.desc"),
      content: (
        <div className="space-y-6">
          <p className="text-sm text-muted-foreground">{t("assertiveness.descTechnique")}</p>
          {DESC_STEPS.map((ds) => (
            <div key={ds.key} className="space-y-2">
              <Label>{ds.label}</Label>
              <p className="text-xs text-muted-foreground">{ds.detail}</p>
              <Textarea placeholder={ds.placeholder} value={formData[ds.key]} onChange={(e) => setFormData({ ...formData, [ds.key]: e.target.value })} rows={2} data-testid={`desc-${ds.key}`} />
            </div>
          ))}
        </div>
      ),
    },
    {
      title: t("assertiveness.steps.practice"),
      content: (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{t("assertiveness.fullResponse")}</Label>
            <Textarea placeholder={t("assertiveness.reformulatePlaceholder")} value={formData.reformulated_response} onChange={(e) => setFormData({ ...formData, reformulated_response: e.target.value })} rows={4} data-testid="reformulated-input" />
          </div>
          <div className="space-y-2">
            <Label>{t("assertiveness.reflectionLabel")}</Label>
            <Textarea placeholder={t("assertiveness.reflectionPlaceholder")} value={formData.reflection} onChange={(e) => setFormData({ ...formData, reflection: e.target.value })} rows={2} data-testid="reflection-input" />
          </div>
        </div>
      ),
    },
  ];

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-8" data-testid="assertiveness-page">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold">{t("assertiveness.title")}</h1>
          <p className="text-muted-foreground text-sm sm:text-base mt-1">{t("assertiveness.subtitle")}</p>
        </div>

        <ExerciseIntro exerciseInfo={EXERCISE_INFO.assertiveness} />

        <Card className="max-w-3xl mx-auto">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-xl">{STEPS_CONFIG[step].title}</CardTitle>
              <span className="text-sm text-muted-foreground">{step + 1}/{STEPS_CONFIG.length}</span>
            </div>
            <div className="flex gap-1.5 mt-3">
              {STEPS_CONFIG.map((_, i) => (
                <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-primary" : "bg-muted"}`} />
              ))}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {STEPS_CONFIG[step].content}
            <div className="flex justify-between pt-4 border-t">
              <Button variant="outline" disabled={step === 0} onClick={() => setStep(step - 1)}>
                <ArrowLeft className="w-4 h-4 mr-1" /> {t("psychoeducation.previous") || t("common.cancel")}
              </Button>
              {step < STEPS_CONFIG.length - 1 ? (
                <Button onClick={() => setStep(step + 1)}>
                  {t("psychoeducation.next") || t("common.save")} <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={submitting} className="bg-primary hover:bg-primary/90" data-testid="submit-assertiveness">
                  <CheckCircle className="w-4 h-4 mr-1" /> {submitting ? t("exercise.saving") : t("common.save")}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      <ShareExerciseDialog open={!!shareData} entryId={shareData?.entryId} exerciseType={shareData?.exerciseType} onClose={closeSharePrompt} />
    </DashboardLayout>
  );
};

export default AssertivenessPage;
